import React, { useCallback, useRef, useState } from 'react';
import { Loader2, Search, X } from 'lucide-react';
import { Button, Input } from '@roger/ui-components';
import { searchAllergies } from '../../services/api';
import { useDebounce } from '../../hooks/useDebounce';

export interface SelectedAllergen {
  fdbAllergenId: string;
  name: string;
}

/**
 * Embedded FDB allergen picker for the importer tool (EMR-1369).
 *
 * Inline rather than a dialog. The clinician is reading Kinnser's raw wording
 * in the panel beside this one while they choose the concept it means, and a
 * modal that covers the source text asks them to hold the evidence in their
 * head instead of reading it.
 *
 * Behaviour matches the allergen combobox on the allergy surface: FDB returns
 * nothing under three characters, so short terms do not spend a request; a
 * monotonic request counter drops out-of-order responses so a slow reply for
 * "sul" cannot overwrite the results for "sulfa".
 *
 * The generated client does not throw on HTTP failures — it resolves with a
 * populated `error` — so failures are handled explicitly rather than by a
 * `catch` that would never run.
 */
const AllergenSearch: React.FC<{
  value: SelectedAllergen | null;
  onSelect: (allergen: SelectedAllergen) => void;
  onClear: () => void;
  placeholder?: string;
  testId?: string;
}> = ({
  value,
  onSelect,
  onClear,
  placeholder = 'Search allergens...',
  testId = 'allergen-search',
}) => {
  const [term, setTerm] = useState('');
  const [results, setResults] = useState<SelectedAllergen[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchFailed, setSearchFailed] = useState(false);
  const requestRef = useRef(0);

  const runSearch = useCallback(async (rawTerm: string) => {
    const search = rawTerm.trim();
    if (search.length < 3) {
      // Bump the counter so an in-flight reply for a longer term cannot land
      // after the field has been cleared back below the threshold.
      requestRef.current++;
      setResults([]);
      setSearchFailed(false);
      setIsLoading(false);
      return;
    }

    const requestId = ++requestRef.current;
    setIsLoading(true);
    const response = await searchAllergies({ query: { limit: 50, search } });

    // A newer search superseded this one while it was in flight.
    if (requestId !== requestRef.current) {
      return;
    }

    if (response.error || !response.data) {
      console.error('Allergen search failed:', response.error);
      setSearchFailed(true);
      setResults([]);
      setIsLoading(false);
      return;
    }

    setSearchFailed(false);
    setResults(
      response.data.items.map(item => ({
        fdbAllergenId: item.fdb_allergen_id,
        name: item.name,
      }))
    );
    setIsLoading(false);
  }, []);

  const debouncedSearch = useDebounce(runSearch, 300);

  const handleTermChange = (next: string) => {
    setTerm(next);
    debouncedSearch(next);
  };

  if (value) {
    return (
      <div
        className='flex items-center justify-between gap-2 rounded-md border bg-muted/40 px-3 py-2'
        data-testid={`${testId}-selected`}
      >
        <span className='text-sm font-medium truncate'>{value.name}</span>
        <Button
          variant='ghost'
          size='sm'
          onClick={() => {
            setTerm('');
            setResults([]);
            onClear();
          }}
          aria-label='Clear selected allergen'
        >
          <X className='h-4 w-4' />
        </Button>
      </div>
    );
  }

  return (
    <div className='space-y-2' data-testid={testId}>
      <div className='relative'>
        <Search className='absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground' />
        <Input
          value={term}
          onChange={e => handleTermChange(e.target.value)}
          placeholder={placeholder}
          className='pl-9'
          inputSize='default'
          aria-label='Search allergens'
        />
        {isLoading && (
          <Loader2 className='absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground' />
        )}
      </div>
      {searchFailed ? (
        <p className='text-sm text-muted-foreground'>
          Search failed. Please try again.
        </p>
      ) : results.length > 0 ? (
        <ul className='max-h-[220px] overflow-auto rounded-md border'>
          {results.map(item => (
            <li key={item.fdbAllergenId}>
              <button
                type='button'
                className='w-full px-3 py-2 text-left text-sm hover:bg-accent hover:text-accent-foreground transition-colors'
                onClick={() => onSelect(item)}
              >
                {item.name}
              </button>
            </li>
          ))}
        </ul>
      ) : term.trim().length > 0 && term.trim().length < 3 ? (
        <p className='text-xs text-muted-foreground'>
          Type at least three characters.
        </p>
      ) : null}
    </div>
  );
};

export default AllergenSearch;
